import {
  CompanyBusinessHoursPutDTO,
  CompanyData,
  CompanyDepartment,
} from "definitions/Company";
import React, { useState } from "react";
import { translate } from "../../../translate/i18n";
import {
  DataTable,
  RowEditParams,
  RowEditSaveParams,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { deleteRequest, putRequest } from "utils/request";
import Swal from "sweetalert2";
import { HeaderTemplateOptions, Panel } from "primereact/panel";
import { NewBusinessHours } from "./components/NewBusinessHours";
import { Calendar } from "primereact/calendar";
import { Ripple } from "primereact/ripple";
import { isArray } from "lodash";
import { multiRegistrosToCsv } from "utils/exportCsv";
import { toast } from "react-toastify";
import { isProductionEnvironment } from "utils/network";

function convertDateToHoursMinutes(date: Date): string {
  return `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
}

function handleExport(departments: CompanyDepartment[]) {
  multiRegistrosToCsv({
    fileName: "horarios-de-funcionamento.csv",
    tableNames: departments.map((dept) => dept.description),
    tables: departments.map((dept) => dept.CompanyBusinessHours),
  });
}

const panelHeaderTemplate = (
  options: HeaderTemplateOptions,
  label: JSX.Element,
  newBusinessHourBoxToggler: () => void
) => {
  const toggleIcon = options.collapsed
    ? "pi pi-chevron-down"
    : "pi pi-chevron-up";
  const className = `${options.className} d-flex justify-content-between`;
  const titleClassName = `${options.titleClassName} p-pl-1`;

  return (
    <div className={className}>
      <button
        className={options.togglerClassName}
        onClick={options.onTogglerClick}
      >
        <span className={toggleIcon}></span>
        <Ripple />
      </button>
      <h4 className={titleClassName}>{label}</h4>
      <button
        className="btn lead rounded-circle"
        style={{
          backgroundColor: "var(--green-500)",
          opacity: 0.7,
          color: "white",
          fontSize: "15px",
          visibility: options.collapsed ? "hidden" : undefined,
        }}
        onClick={newBusinessHourBoxToggler}
      >
        +
      </button>
    </div>
  );
};

const modalConfirmRemovalBusinessHour = (
  businessHourId: number,
  triggerReactivity: () => void
) => {
  Swal.fire({
    title: translate("attemption"),
    text: translate("deleteHours"),
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: translate("confirmEraseCompany"),
  }).then((result) => {
    if (result.isConfirmed) {
      deleteRequest(
        `/companiesbusinesshours?businessHourId=${businessHourId}`
      ).then(function (response) {
        if (response.success) {
          triggerReactivity();
        }
      });
    }
  });
};

type CompanyDepartmentHoursProps = {
  department: CompanyDepartment;
  triggerReactivity: () => void;
};

function CompanyDepartmentHours({
  department,
  triggerReactivity,
}: CompanyDepartmentHoursProps) {
  const [creationMode, setCreationMode] = useState(false);

  const toggleCreationMode = (choice: boolean) => {
    setCreationMode(choice);
  };

  const [startTime, setStartTime] = useState<Date>();
  const [endTime, setEndTime] = useState<Date>();

  const beginEditionHandler = (params: RowEditParams) => {
    setStartTime(new Date(`2024-01-01T${params.data.startTime}`));
    setEndTime(new Date(`2024-01-01T${params.data.endTime}`));
  };

  const saveEditionHandler = (params: RowEditSaveParams) => {
    if (!startTime || !endTime) return;
    putRequest("/companiesbusinesshours", {
      id: params.data.id,
      startTime: convertDateToHoursMinutes(startTime),
      endTime: convertDateToHoursMinutes(endTime),
    } as CompanyBusinessHoursPutDTO).then((result) => {
      if (result.success) {
        triggerReactivity();
      }
    });
  };

  return (
    // disable typescript because this component's typing doesn't mention children, but it needs them:
    // https://www.primefaces.org/primereact-v6/#/datatable
    // @ts-ignore
    <Panel
      headerTemplate={(options) =>
        panelHeaderTemplate(
          options,
          <span className="d-flex flex-column align-items-center">
            <span>
              {department.description}
              {isProductionEnvironment() ? "" : "-" + department.id}
            </span>
            {department.default && (
              <span style={{ fontSize: 10 }} className="text-info">
                {`(${translate("default")})`}
              </span>
            )}
            <br />
            {!department.CompanyBusinessHours.length && (
              <span style={{ fontSize: 10 }} className="text-danger">
                {" - "} *Se estiver vazio, este departamento usará os horários
                do departamento padrão
              </span>
            )}
          </span>,
          () => {
            setCreationMode((prev) => !prev);
          }
        )
      }
      toggleable
      collapsed={!department.default}
      className="mb-3"
    >
      <div className="mb-4">
        <div className="d-flex justify-content-between"></div>
        {creationMode && (
          <NewBusinessHours
            CompanyDepartmentId={department.id}
            toggleCreationMode={toggleCreationMode}
            triggerReactivity={triggerReactivity}
            daysAlreadySavedInDB={department.CompanyBusinessHours.map(
              (bh) => bh.day
            )}
          />
        )}

        <div>
          {/* @ts-ignore bc this component's typing doesn't mention children, but it has them: https://www.primefaces.org/primereact-v6/#/datatable */}
          <DataTable
            dataKey="id"
            value={department.CompanyBusinessHours.toSorted(
              (a, b) => a.day - b.day
            )}
            emptyMessage={translate("businessHoursNotFound")}
            editMode="row"
            onRowEditInit={beginEditionHandler}
            onRowEditSave={saveEditionHandler}
          >
            <Column
              field="startTime"
              header={translate("startHour")}
              sortable
              editor={() => (
                <Calendar
                  value={startTime}
                  onChange={(e) => {
                    const date = isArray(e.value) ? e.value[0] : e.value;
                    if (date && endTime && +date < +endTime) {
                      setStartTime(date);
                    } else {
                      toast.info(
                        translate("warningStartTimeGreaterThanEndTime")
                      );
                    }
                  }}
                  timeOnly
                />
              )}
            ></Column>
            <Column
              field="endTime"
              header={translate("endHour")}
              sortable
              editor={() => (
                <Calendar
                  value={endTime}
                  onChange={(e) => {
                    const date = isArray(e.value) ? e.value[0] : e.value;
                    if (date && startTime && +date > +startTime) {
                      setEndTime(date);
                    } else {
                      toast.info(translate("warningEndTimeLessThanStartTime"));
                    }
                  }}
                  timeOnly
                />
              )}
            ></Column>
            <Column header={translate("days")} field="dayDescription"></Column>
            <Column header={translate("edit")} rowEditor colSpan={1}></Column>
            {!department.default && (
              <Column
                body={(data) => (
                  <div
                    className="action-icon"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      modalConfirmRemovalBusinessHour(
                        data.id,
                        triggerReactivity
                      );
                    }}
                  >
                    <i className="mdi mdi-delete" style={{ fontSize: 20 }}></i>
                  </div>
                )}
                header={translate("delete")}
              ></Column>
            )}
          </DataTable>
        </div>
      </div>
    </Panel>
  );
}

type TabHoursProps = {
  companyData: CompanyData;
  handleUpdate: (tabNumber: number) => void;
};
export default function TabHours({ companyData, handleUpdate }: TabHoursProps) {
  const triggerReactivity = () => {
    handleUpdate(3);
  };

  if (!companyData || !companyData.CompanyDepartments) {
    return <div>empresa sem departamentos configurados</div>;
  }

  return (
    <div className="d-flex flex-column">
      <button
        type="button"
        onClick={() => handleExport(companyData.CompanyDepartments || [])}
        className="export-excel ml-auto mb-2"
      >
        <i
          style={{ fontSize: 16, marginRight: 5 }}
          className="pi pi-file-excel"
        />
        <span style={{ fontSize: "1vw" }}>{translate("exportAllData")}</span>
      </button>
      {/* exibir somente deptos ativos, ordenados alfabeticamente e mantendo o depto padrão como primeiro da lista */}
      {companyData.CompanyDepartments.filter((dept) => dept.active)
        .toSorted((dept1, dept2) =>
          dept1.description.toLowerCase() < dept2.description.toLowerCase()
            ? -1
            : 1
        )
        .toSorted((dept1) => (dept1.default ? -1 : 1))
        .map((dpt) => (
          <CompanyDepartmentHours
            department={dpt}
            key={dpt.id}
            triggerReactivity={triggerReactivity}
          />
        ))}
    </div>
  );
}
